import { useRef } from 'react';
import { createStore, StoreApi } from 'zustand';
import { Message } from '@/widget/domain/message';
import { Operator } from '@/widget/domain/operator';
import { Conversation } from '@/widget/domain/conversation';

export interface WidgetState {
  open: boolean;
  previousMode: 'main' | 'list',
  mode: 'main' | 'list' | 'conversation';
  touched: boolean;
  templateToStart: string | null;
  delayedMessage: Message | null;

  setDelayedMessage: (value: Message) => void;
  setOpen: (value: boolean) => void;
  setMode: (mode: 'main' | 'list' | 'conversation') => void;
  setTouched: (value: boolean) => void;
  setTemplateToStart: (value: string) => void;
}

interface DataState {
  operators: Operator[];
  conversations: Conversation[];
  messages: Message[] | null,
  conversationKeys: string[];
  activeConversationKey: string,
  operatorsLoaded: boolean;
  conversationsLoaded: boolean;
  contactRequested: boolean;
  contactRecognized: boolean;
  userId: number | null;
  accountId: number | null;
  contactId: number | null;
  companyId: number | null;
  orgName: string | null;

  setMessages: (messages: Message[]) => void;
  setConversations: (conversations: Conversation[], conversationsLoaded: boolean) => void;
  setOperators: (operators: Operator[], operatorsLoaded: boolean) => void;
  setConversationKeys: (keys: string[]) => void;
  setActiveConversationKey: (key: string) => void;
  setContactRequested: (val: boolean) => void;
  setContactRecognized: (val: boolean) => void;
  setContactId: (val: number) => void;
  setContactInfo: (val: {
    contactId: number;
    companyId: number;
    conversationKeys: string[];
  }) => void;
}

interface SubscriptionState {
  widgetSubscription: ActionCable.Channel | null;
  setWidgetSubscription: (val: ActionCable.Channel) => void;
}

export default function useChatWidgetStore({
  mode, open, userId, accountId, orgName,
}: {
  open: boolean;
  previousMode: 'main' | 'list';
  mode: 'main' | 'list' | 'conversation';
  userId: number | null;
  accountId: number | null;
  orgName: string | null;
}) {
  return useRef(createStore<WidgetState & DataState & SubscriptionState>((set) => ({
    previousMode: 'main',
    mode,
    open,
    touched: false,
    templateToStart: null,
    delayedMessage: null,

    setDelayedMessage: (delayedMessage) => set(() => ({ delayedMessage })),
    setOpen: (value) => set(() => ({ open: value })),
    setMode: (mode) => set((state) => {
      const previousMode = state.mode === 'conversation' ? 'main' : state.mode;
      return { previousMode, mode };
    }),
    setTouched: (value) => set(() => ({ touched: value })),
    setTemplateToStart: (value) => set(() => ({ templateToStart: value })),

    // Widget data
    operators: [],
    operatorsLoaded: false,
    conversations: [],
    conversationsLoaded: false,
    activeConversationKey: undefined,
    messages: [],
    conversationKeys: [],
    contactRequested: false,
    contactRecognized: false,
    userId,
    accountId,
    contactId: null,
    companyId: null,
    orgName,

    setMessages: (messages) => set(() => ({ messages })),
    setConversations: (conversations, conversationsLoaded) => set(() => ({
      conversations,
      conversationsLoaded,
    })),
    setOperators: (operators, operatorsLoaded) => set(() => ({
      operators,
      operatorsLoaded,
    })),
    setConversationKeys: (keys) => set(() => ({ conversationKeys: keys })),
    setActiveConversationKey: (key) => set(() => ({ activeConversationKey: key })),
    setContactRequested: (val) => set(() => ({ contactRequested: val })),
    setContactRecognized: (val) => set(() => ({ contactRecognized: val })),
    setContactId: (contactId) => set(() => ({ contactId })),
    setContactInfo: ({
      contactId, companyId, conversationKeys,
    }) => set(() => ({ contactId, companyId, conversationKeys })),

    // Subscription info
    widgetSubscription: null,
    setWidgetSubscription: (val) => set(() => ({ widgetSubscription: val })),
  }))).current;
}

export type DataStore = WidgetState & DataState & SubscriptionState;
export type ChatWidgetStore = StoreApi<DataStore>;
